// src/App.js
import React, { useEffect, useMemo } from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation
} from 'react-router-dom';

import { ConnectionProvider, WalletProvider } from '@solana/wallet-adapter-react';
import { WalletAdapterNetwork } from '@solana/wallet-adapter-base';
import { WalletModalProvider } from '@solana/wallet-adapter-react-ui';

import { PhantomWalletAdapter } from '@solana/wallet-adapter-phantom';
import {
  SolflareWalletAdapter,
  TorusWalletAdapter,
  LedgerWalletAdapter
} from '@solana/wallet-adapter-wallets';

import { WalletConnectWalletAdapter } from '@solana/wallet-adapter-walletconnect';

import '@solana/wallet-adapter-react-ui/styles.css';

import Header         from './components/Header';
import MarketLobby    from './components/MarketLobby';
import Leaderboard    from './components/Leaderboard';
import TournamentCard from './components/TournamentCard';
import Footer         from './components/Footer';
import RoadmapCard         from './components/RoadmapCard';
import TokenDetails         from './components/TokenDetails';

import BetConfirmationPage from './components/BetConfirmationPage';
import BetSuccessPage from './pages/BetSuccessPage'; 

import Signup    from './pages/Signup';
import Login     from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import Profile   from './pages/Profile';
import ModelInfo from './pages/ModelInfo';
import BuyChips  from './pages/BuyChips';
import MyBetsPage from './pages/MyBetsPage';
import P2P from './pages/P2P';
import Swap from './pages/Swap';
import AdminSwaps from './pages/AdminSwaps';
import AdminLotteryWinners from './pages/AdminLotteryWinners';

import Casino from './components/Casino/Casino';
import ScatterSlots from './components/Casino/ScatterSlots';
import ComingSoon from './components/Casino/ComingSoon';
import HighCardDuel from './components/Casino/HighCardDuel';
// import Baccarat from './components/Casino/Baccarat';
import SS from './components/Casino/SS';
import TexasHoldemPoker from './components/Casino/Texas';
import Lottery         from './components/Casino/Lottery';
import Raffle         from './components/Casino/Raffle';
import Roulette         from './components/Casino/Roulette';
import BankBluff         from './components/Casino/BankBluff';
import Plinko          from './components/Casino/Plinko';  // ADD THIS IMPORT

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function RequireAuth({ children }) {
  const token = localStorage.getItem('token');
  const location = useLocation();
  if (!token) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  return children;
}

function Home() {
  return (
    <>
      <div id="content-top" />
      <MarketLobby />
      <Leaderboard />
      <TournamentCard />
      <TokenDetails />
      <RoadmapCard />
    </>
  );
}

export default function App() {
  const network = WalletAdapterNetwork.Mainnet;
  const endpoint = useMemo(
    () => process.env.REACT_APP_SOLANA_RPC_URL
          || 'https://solana-mainnet.g.alchemy.com/v2/pbGjxwq4DDspjqXb8DkO-Q4H-SzdtrLC',
    []
  );

  // Detect mobile
  const isMobile = useMemo(() => 
    /Android|iPhone|iPad|iPod/.test(navigator.userAgent),
  []);

  // WalletConnect adapter with deep-link support
  const walletConnectAdapter = useMemo(() => {
    return new WalletConnectWalletAdapter({
      network,
      options: {
        // standard WalletConnect v2 relay/project setup
        relayUrl: 'wss://relay.walletconnect.com',
        projectId: process.env.REACT_APP_WALLET_CONNECT_PROJECT_ID,
        metadata: {
          name: 'Prediction Arena',
          description: 'Trade and play with Arena tokens',
          url: window.location.origin,
          icons: [`${window.location.origin}/logo192.png`]
        },
      },
      // on mobile, auto-deep-link instead of only QR-scan
      onUri: (uri) => {
        if (isMobile) {
          // percent-encode per WalletConnect mobile linking best practice
          const encoded = encodeURIComponent(uri);
          window.location.href = uri;
        }
      }
    });
  }, [network, isMobile]);

  const wallets = useMemo(() => [
    new PhantomWalletAdapter({
      openUrl: (url) => window.location.assign(url),
      redirectUri: window.location.origin
    }),
    new SolflareWalletAdapter(),
    new TorusWalletAdapter(),
    new LedgerWalletAdapter(),
    walletConnectAdapter
  ], [walletConnectAdapter]);

  return (
    <ConnectionProvider endpoint={endpoint} config={{ commitment: 'confirmed', wsEndpoint: null }}>
      <WalletProvider wallets={wallets} autoConnect>
        <WalletModalProvider>
          <BrowserRouter>
            <ScrollToTop />

            <div className="flex flex-col min-h-screen bg-gray-900 text-white">
              <Header />

              <main className="flex-grow container mx-auto px-4 py-8 pt-24">
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/model-info" element={<ModelInfo />} />
                  <Route path="/signup" element={<Signup />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/buy-chips" element={<BuyChips />} />
                  <Route path="/bet-confirmation" element={<BetConfirmationPage />} />
                  <Route path="/bet-success" element={<BetSuccessPage />} />
                  <Route path="/casino/texas-holdem" element={<TexasHoldemPoker />} />
                  <Route path="/casino/lottery" element={<Lottery />} />
                  <Route path="/casino/raffle" element={<Raffle />} />
                  <Route path="/casino/roulette" element={<Roulette />} />
                  <Route path="/casino/bankbluff" element={<BankBluff />} />
                  <Route path="/casino/plinko" element={<Plinko />} />  {/* ADD THIS ROUTE */}
                  <Route
                    path="/profile"
                    element={
                      <RequireAuth>
                        <Profile />
                      </RequireAuth>
                    }
                  />
                  {/* Add the new Swap route */}
                  <Route
                    path="/swap"
                    element={
                      <RequireAuth>
                        <Swap />
                      </RequireAuth>
                    }
                  />
                  {/* Admin Routes */}
                  <Route
                    path="/admin/swaps"
                    element={
                        <AdminSwaps />
                    }
                  />
                  <Route
                    path="/admin/lottery"
                    element={
                        <AdminLotteryWinners />
                    }
                  />
                  <Route path="*" element={<Navigate to="/" replace />} />
                  <Route path="/my-bets" element={<MyBetsPage />} />

                  <Route path="/casino" element={<Casino />} />
                  <Route path="/casino/scatter-slots" element={<ScatterSlots />} />
                  <Route path="/casino/high-card-duel" element={<HighCardDuel />} />
                  <Route path="/casino/high-card-duel/:id" element={<HighCardDuel />} />
                  {/* <Route path="/casino/baccarat" element={<Baccarat />} /> */}
                  <Route path="/casino/ss" element={<SS />} />
                  <Route path="/casino/:game" element={<ComingSoon />} />
                  <Route path="/p2p" element={<P2P />} />
                </Routes>
              </main>

              <Footer />
            </div>
          </BrowserRouter>
        </WalletModalProvider>
      </WalletProvider>
    </ConnectionProvider>
  );
}
